import { useState } from 'react';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { useEditorContext } from 'components/editor';
import { ConfirmButton } from 'components/common';

interface Props {
  blockData: any;
  blockId: number;
  updateData: (data: any) => void;
}

/**
 * Renders a button that generates AI translations for a single block
 * in the currently active target language version.
 */
const EditorBlockGenerateTranslations = ({
  blockData,
  blockId,
  updateData
}: Props) => {
  const { editorActiveLanguageVersion, editorAIEnabled, editorPreviewType } =
    useEditorContext();

  const {
    messages,
    pageState: { course }
  } = useReduxState();

  const { dispatch } = useDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const parseTranslatedData = (
    translatedData: Record<string, string>
  ): Record<string, string> => {
    const translatable: Record<string, string> = {};
    Object.entries(translatedData).forEach(([key, value]) => {
      const parts = key.split('__');
      if (parts.length >= 4) {
        const field = parts[3];
        translatable[field] = value;
      }
    });
    return translatable;
  };

  const handleConfirm = async () => {
    setIsLoading(true);

    const response = await api.post(
      `/admin/courses/${course.id}/language-versions/${editorActiveLanguageVersion}/ai-translations/generate`,
      { scope: 'block', blockId }
    );

    setIsLoading(false);

    if (!response.data?.success) {
      dispatch.handleErrorDispatch(null);
      return false;
    }

    const translatable = parseTranslatedData(
      response.data.translatedData ?? {}
    );

    updateData({
      ...blockData,
      locales: {
        ...blockData.locales,
        [editorActiveLanguageVersion]: {
          ...blockData.locales?.[editorActiveLanguageVersion],
          translatable: {
            ...blockData.locales?.[editorActiveLanguageVersion]?.translatable,
            ...translatable
          }
        }
      }
    });

    return true;
  };

  if (!editorActiveLanguageVersion || editorPreviewType) return null;

  const hasSourceContent = Object.values(
    blockData.locales?.[course.language]?.translatable ?? {}
  ).some((value) => typeof value === 'string' && value.trim());

  const isEnabled = hasSourceContent && editorAIEnabled;

  if (!isEnabled) return null;

  return (
    <div className="block-generate-translations">
      <ConfirmButton
        actionText={messages.commonTranslate}
        buttonCategory="light"
        buttonIcon="wand-magic-sparkles"
        buttonSize="small"
        buttonText={messages.commonTranslate}
        confirmText={messages.editorBlockGenerateTranslationsOverwrite}
        confirmTitle={messages.aiGenerateTranslationsTranslateWithAi}
        disabled={isLoading}
        onConfirm={handleConfirm}
      />
      <style jsx>{`
        .block-generate-translations {
          display: flex;
          justify-content: flex-end;
          margin-top: 8px;
          visibility: hidden;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockGenerateTranslations;
